import React, {
  useEffect,
  useState,
  PropsWithChildren,
  useContext,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar, SidebarCTA, SidebarLogo } from "flowbite-react";
import Header from "../Header";
import { TbDashboardFilled } from "react-icons/tb";
import { FaUser } from "react-icons/fa";
import { FaFileImport } from "react-icons/fa";
import { FaQuestionCircle } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { HiSpeakerphone } from "react-icons/hi";
import { BsSend } from "react-icons/bs";
import { LuPlus } from "react-icons/lu";
import { FiLogOut } from "react-icons/fi";
import { PiListBulletsFill } from "react-icons/pi";
import { FaVideo } from "react-icons/fa";
import { logout } from "../../store/reducers/authSlice";
import axios from "../../util/axios";
import { Context } from "../Context/ErrorContext";
import CustomButton from "../Button";
import { Grid } from "@mui/material";
import CustomTextField from "../TextField";
import CustomModal from "../Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import PhoneInput from "../PhoneInput";
import { isOnlyCountryCode } from "../../util/util";

interface ContentLayoutProps extends PropsWithChildren {
  className?: string;
}

const navAdminItems = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/",
    icon: TbDashboardFilled,
    children: [],
    id: 1,
  },
  {
    key: "employees",
    label: "Employees",
    path: "/employee",
    icon: FaUser,
    children: [],
    id: 2,
  },
  // {
  //   key: "company",
  //   label: "Company",
  //   path: "/company",
  //   icon: FaUsers,
  //   children: [],
  // },
  // {
  //   key: "socialMedia",
  //   label: "Social Impact Media/Press",
  //   path: "/socialimpact",
  //   icon: HiSpeakerphone,
  //   children: [],
  //   id: 3,
  // },
  {
    key: "inviteUploadFile",
    label: "Invite / Upload File",
    path: "/inviteuploadfile",
    icon: FaFileImport,
    children: [],
    id: 4,
  },
  {
    key: "companySafetyPolicy",
    label: "Company Safety Policy",
    path: "/company-safety-policy",
    icon: PiListBulletsFill,
    children: [],
    id: 5,
  },
  {
    key: "farsDfarsCompliance",
    label: "FARS/DFARS 2026 Readiness",
    path: "/fars-dfars-compliance",
    icon: HiSpeakerphone,
    children: [],
    id: 8,
  },
  {
    key: "companyCertifications",
    label: "Company Certifications",
    path: "/company-certifications",
    icon: TbDashboardFilled,
    children: [],
    id: 9,
  },
  {
    key: "onMyWaySupport",
    label: "OnMyWay Support",
    path: "https://onmywayhelp.zendesk.com/hc/en-us/requests/new",
    icon: FaQuestionCircle,
    children: [],
    id: 6,
  },
  {
    key: "videos",
    label: "First Responder Videos",
    path: "/videos",
    icon: FaVideo,
    children: [],
    id: 7,
  },
];

const ContentLayout = ({ children, className = "" }: ContentLayoutProps) => {
  const location = useLocation();
  const pathName: string = location.pathname;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setError } = useContext(Context);
  const [activeNav, setActiveNav] = useState("");
  const [isInviteModal, setInviteModal] = useState(false);
  const side = localStorage.getItem("sidenav");
  const [sideNavOpen, setSideNavOpen] = useState(side ? side === "true" : true);

  const [country, setCountry] = useState<any>({});

  useEffect(() => {
    navAdminItems.forEach((item) => {
      if (item.path === pathName || pathName.includes(item.path)) {
        setActiveNav(item.key);
      }
    });
  }, [pathName]);

  const toggleSideNav = () => {
    localStorage.setItem("sidenav", (!sideNavOpen).toString());
    setSideNavOpen((prev) => !prev);
  };

  const logoutHandler = () => {
    localStorage.removeItem("user_token");
    dispatch(logout());
    navigate("/login");
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().required("Email is required"),
    phone: Yup.string().required("phone is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, {setFieldError}) => {
      try {
        if (values.phone === "" || isOnlyCountryCode(values.phone, country)) {
          setFieldError("phone", "Required");
        } else {
          axios({
            URL: "/omw/subAdmin/createUser",
            method: "POST",
            data: values,
          })
            .then(() => {
              resetForm();
              setInviteModal(false);
            })
            .catch((err) => setError(err.response?.data?.message));
        }
      } catch (e) {}
    },
  });

  const { handleSubmit, values, handleChange, setFieldValue, resetForm, errors } =
    formik;

  const handleMenuClick = (tab: any, path: any) => {
    if (tab === "onMyWaySupport") {
      window.open(path, `_blank`);
    } else {
      navigate(path);
    }
  };

  return (
    <div className={"flex h-screen bg-primary"}>
      <div className={"flex flex-col h-full min-h-0 gap-2 shrink-0"}>
        <Sidebar
          aria-label="Sidebar with multi-level dropdown example"
          className={`bg-primary transition-width duration-100 !h-auto flex-1 min-h-0 overflow-hidden ${
            sideNavOpen ? "w-[256px]" : "w-min"
          }`}
        >
          <SidebarLogo
            href="/"
            img={"/logo.png"}
            imgAlt="logo"
            className={`w-full p-4 !pt-2 max-h-[75px] h-full items-center ${
              sideNavOpen ? "flex" : "hidden"
            }`}
          />
          <SidebarLogo
            href="/"
            img={"/OMW_logo.png"}
            imgAlt="logo"
            className={`w-full p-0 max-h-[75px] h-full items-center ${
              sideNavOpen ? "hidden" : "flex"
            }`}
          />
          <Sidebar.Items>
            <Sidebar.ItemGroup className={"m-0 p-0"}>
              {navAdminItems.map((item: any, i) => (
                  <Sidebar.Item
                    key={item?.id}
                    className={`relative p-0 flex items-center justify-start h-[50px] text-2xl cursor-pointer ${
                      activeNav === item.key
                        ? "text-sidebarSelectText bg-gray"
                        : "text-primaryText"
                    } ${sideNavOpen ? "gap-2" : ""}`}
                    onClick={() => handleMenuClick(item.key, item.path)}
                  >
                    {activeNav === item.key && (
                      <div
                        className={
                          "w-1 h-10 bg-sidebarSelectText absolute -left-2.5 top-1.5 rounded"
                        }
                      />
                    )}
                    <div className={"flex items-center gap-4"}>
                      <item.icon />
                      <p
                        className={`text-left font-semibold flex text-wrap text-sm ${
                          sideNavOpen ? "flex" : "hidden"
                        }`}
                      >
                        {item.label}
                      </p>
                    </div>
                  </Sidebar.Item>
                ))}
            </Sidebar.ItemGroup>
            <Sidebar.ItemGroup>
              <SidebarCTA
                className={`bg-gray justify-center flex-col items-center gap-2 py-6 ${
                  sideNavOpen ? "flex" : "hidden"
                }`}
              >
                <BsSend className={"text-sidebarSelectText text-4xl"} />
                <span className={"text-primaryText text-2xl font-semibold"}>
                  Send Quick <br />
                  Invite
                </span>
                <CustomButton
                  title={
                    <>
                      <LuPlus /> Invite Employee
                    </>
                  }
                  className={"gap-2 font-semibold text-base uppercase"}
                  onClick={() => setInviteModal(true)}
                />
              </SidebarCTA>
            </Sidebar.ItemGroup>
          </Sidebar.Items>
        </Sidebar>
        <div
          className={
            "py-3 px-4 text-primaryText flex flex-row gap-2 justify-start items-center w-fit cursor-pointer shrink-0"
          }
          onClick={logoutHandler}
        >
          <FiLogOut />
          <p
            className={`text-left ml-2 font-semibold ${
              sideNavOpen ? "flex" : "hidden"
            }`}
          >
            Sign Out
          </p>
        </div>
      </div>

      <div className={"flex flex-col w-full h-full"}>
        <Header toggleSideNav={toggleSideNav} isSideNavOpen={sideNavOpen} />
        <div className={`flex flex-1 min-h-0 p-4 flex-col bg-gray ${className}`}>
          {children}
        </div>
      </div>

      <CustomModal
        open={isInviteModal}
        onClose={() => {
          setInviteModal(false);
          resetForm();
        }}
        label={"Invite Employee"}
      >
        <form
          onSubmit={handleSubmit}
          className={"flex flex-col mt-5 w-full max-w-full gap-4"}
          autoComplete={"off"}
        >
          <Grid container spacing={2} className={"overflow-auto max-h-[600px]"}>
            <Grid item xs={12} sm={8} md={12}>
              <CustomTextField
                label={"Name"}
                type={"text"}
                name={"name"}
                onChange={handleChange}
                required
                value={values?.name}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={12}>
              <CustomTextField
                label={"Email"}
                type={"text"}
                name={"email"}
                onChange={handleChange}
                required
                value={values?.email}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <PhoneInput
                label={"Phone"}
                onChange={(v) => setFieldValue("phone", v)}
                value={values?.phone}
                countryChange={setCountry}
                required
                errorMsg={errors?.phone || ""}
              />
            </Grid>
          </Grid>
          <CustomButton
            title={"Invite Employee"}
            type={"submit"}
            className={"!py-3"}
          />
        </form>
      </CustomModal>
    </div>
  );
};

export default ContentLayout;
